import log from '../../../lib/log';
import Menu from '../../menu';
import { ABILITY_META } from '../../../data/ability-metadata';

import { Modules, Opcodes } from '@kaetram/common/network';

import type { AbilityMeta } from '../../../data/ability-metadata';
import type Ability from '../../../entity/character/player/ability';
import type Player from '../../../entity/character/player/player';

interface AbilityElement extends HTMLElement {
    key?: string;
}

export type SelectCallback = (type: Opcodes.Ability, key: string, index?: number) => void;

export default class Abilities extends Menu {
    private activeAbilities: HTMLUListElement = document.querySelector('#active-abilities')!;
    private passiveAbilities: HTMLUListElement = document.querySelector('#passive-abilities')!;

    private cooldownTimers = new Map<string, { overlay: HTMLElement; interval: number }>();

    private tooltip: HTMLElement;

    private selectCallback?: SelectCallback;

    public constructor(private player: Player) {
        super('#abilities-page');

        for (let i = 0; i < this.activeAbilities.children.length; i++)
            this.activeAbilities.children[i].addEventListener('click', () => this.handleAction(i));

        this.tooltip = document.createElement('div');
        this.tooltip.classList.add('ability-tooltip');
        document.body.append(this.tooltip);

        this.activeAbilities.addEventListener('mouseover', (e: MouseEvent) =>
            this.handleTooltipOver(e)
        );
        this.activeAbilities.addEventListener('mouseout', () => this.hideTooltip());
        this.passiveAbilities.addEventListener('mouseover', (e: MouseEvent) =>
            this.handleTooltipOver(e)
        );
        this.passiveAbilities.addEventListener('mouseout', () => this.hideTooltip());
    }

    public override synchronize(): void {
        this.hideAll();

        let activeIndex = 0,
            passiveIndex = 0;

        for (let ability of Object.values(this.player.abilities))
            switch (ability.type) {
                case Modules.AbilityType.Active: {
                    this.setActiveAbility(activeIndex, ability.key, ability.level, ability.active);
                    activeIndex++;
                    break;
                }

                case Modules.AbilityType.Passive: {
                    this.setPassiveAbility(passiveIndex, ability.key, ability.level);
                    passiveIndex++;
                    break;
                }
            }
    }

    private handleAction(index: number): void {
        let ability = this.activeAbilities.children[index] as AbilityElement;

        if (ability.style.display === 'none' || !ability.key) return;

        this.selectCallback?.(Opcodes.Ability.Use, ability.key);
    }

    private handleTooltipOver(e: MouseEvent): void {
        let target = (e.target as HTMLElement).closest('.ability') as AbilityElement | null;

        if (!target?.key) return;

        let meta = ABILITY_META[target.key];

        if (!meta) return;

        let playerAbility = this.player.abilities[target.key],
            type = playerAbility?.type === Modules.AbilityType.Active ? 'Activa' : 'Pasiva';

        this.showTooltip(meta, type, target);
    }

    private showTooltip(meta: AbilityMeta, type: string, anchor: HTMLElement): void {
        this.tooltip.innerHTML =
            `<span class="tooltip-name">${meta.name}</span>` +
            `<span class="tooltip-type">${type}</span>` +
            `<span class="tooltip-desc">${meta.description}</span>`;

        this.tooltip.style.display = 'flex';

        let rect = anchor.getBoundingClientRect();

        this.tooltip.style.left = `${rect.right + 6}px`;
        this.tooltip.style.top = `${rect.top}px`;

        let tooltipRect = this.tooltip.getBoundingClientRect();

        if (tooltipRect.right > window.innerWidth)
            this.tooltip.style.left = `${rect.left - tooltipRect.width - 6}px`;

        if (tooltipRect.bottom > window.innerHeight)
            this.tooltip.style.top = `${window.innerHeight - tooltipRect.height - 4}px`;
    }

    private hideTooltip(): void {
        this.tooltip.style.display = 'none';
    }

    private setAbility(ability: AbilityElement, key: string, level = 1, passive = false): void {
        ability.innerHTML = '';
        ability.className = 'ability';
        ability.key = key;

        ability.style.display = 'block';

        let icon = document.createElement('div');

        icon.classList.add('ability-icon');
        icon.classList.add(`ability-icon-${key}`);

        icon.draggable = true;

        if (!passive)
            icon.addEventListener('dragstart', (e: DragEvent) => {
                e.dataTransfer?.setData('ability-key', key);
            });

        if (level > 4) level = 4;
        if (level < 1) level = 1;

        for (let i = 0; i < level; i++) {
            let level = document.createElement('div');

            level.classList.add('ability-level');
            level.classList.add(`ability-level${i + 1}`);

            ability.append(level, icon);
        }
    }

    public setActiveAbility(index: number, key: string, level = 1, active = false): void {
        let element = this.activeAbilities.children[index] as AbilityElement;

        if (!element) return log.error(`Could not find ability with index ${index}, key: ${key}.`);

        if (!key) return this.hideAbility(element);

        this.setAbility(element, key, level);

        let icon = element.querySelector('.ability-icon')!;

        if (!icon) return;

        if (active) icon.classList.add('active');
        else icon.classList.remove('active');

        let playerAbility = this.player.abilities[key];

        if (playerAbility?.isOnCooldown()) this.addCooldownOverlay(element, key, playerAbility);
    }

    public setPassiveAbility(index: number, key: string, level = 1): void {
        let ability = this.passiveAbilities.children[index] as AbilityElement;

        if (!ability) return log.error(`Could not find ability with index ${index}, key: ${key}.`);

        if (!key) return this.hideAbility(ability);

        this.setAbility(ability, key, level, true);
    }

    private addCooldownOverlay(element: HTMLElement, key: string, ability: Ability): void {
        this.removeCooldownOverlay(key);

        let overlay = document.createElement('div');
        overlay.classList.add('ability-cooldown');

        let text = document.createElement('span');
        text.classList.add('cooldown-text');
        overlay.append(text);

        element.append(overlay);

        this.updateCooldownText(text, ability);

        let interval = window.setInterval(() => {
            if (!ability.isOnCooldown()) {
                this.removeCooldownOverlay(key);
                return;
            }

            this.updateCooldownText(text, ability);
        }, 250);

        this.cooldownTimers.set(key, { overlay, interval });
    }

    private removeCooldownOverlay(key: string): void {
        let timer = this.cooldownTimers.get(key);

        if (!timer) return;

        clearInterval(timer.interval);
        timer.overlay.remove();
        this.cooldownTimers.delete(key);
    }

    private clearAllCooldowns(): void {
        for (let [, timer] of this.cooldownTimers) {
            clearInterval(timer.interval);
            timer.overlay.remove();
        }

        this.cooldownTimers.clear();
    }

    private updateCooldownText(element: HTMLElement, ability: Ability): void {
        let remaining = Math.ceil(ability.getCooldownRemaining() / 1000);

        if (remaining >= 60) {
            let mins = Math.floor(remaining / 60),
                secs = remaining % 60;
            element.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
        } else element.textContent = `${remaining}s`;
    }

    private hideAbility(ability: HTMLElement): void {
        ability.style.display = 'none';
    }

    private hideAll(): void {
        this.clearAllCooldowns();

        for (let child of this.activeAbilities.children) this.hideAbility(child as HTMLElement);

        for (let child of this.passiveAbilities.children) this.hideAbility(child as HTMLElement);
    }

    public onSelect(callback: SelectCallback): void {
        this.selectCallback = callback;
    }
}
